// frontend/src/utils/rbac.js
// RBAC Utility Functions for Frontend
// Purpose: Provides role-based access control utilities for React components

// Role constants (matching backend)
export const ROLES = {
  USER: "user",
  MODERATOR: "moderator",
  ADMIN: "admin",
  SUPER_ADMIN: "super_admin",
};

// Role levels (matching backend)
export const ROLE_LEVELS = {
  [ROLES.USER]: 1,
  [ROLES.MODERATOR]: 2,
  [ROLES.ADMIN]: 3,
  [ROLES.SUPER_ADMIN]: 4,
};

// Permission constants (matching backend)
export const PERMISSIONS = {
  // User permissions
  VIEW_DEALS: "view_deals",
  TRACK_ITEMS: "track_items",
  VIEW_PROFILE: "view_profile",

  // Moderator permissions
  MODERATE_CONTENT: "moderate_content",
  VIEW_ANALYTICS: "view_analytics",

  // Admin permissions
  MANAGE_USERS: "manage_users",
  MANAGE_DATABASE: "manage_database",
  MANAGE_SCRAPER: "manage_scraper",
  VIEW_LOGS: "view_logs",
  RESTART_BACKEND: "restart_backend",

  // Super Admin permissions
  MANAGE_ADMINS: "manage_admins",
  SYSTEM_CONFIG: "system_config",
  FULL_ACCESS: "full_access",
};

/**
 * Parse permissions string into array
 * @param {string} permissionsString - Comma-separated permissions string
 * @returns {Array} Array of permission strings
 */
export const parsePermissions = (permissionsString) => {
  if (!permissionsString) return [];
  return permissionsString
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p);
};

/**
 * Check if user has a specific permission
 * @param {Object} user - User object with role, role_level, permissions
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;

  // For malformed users (missing role_level), be more strict
  // Only allow access if they have an explicit role_level
  if (user.role_level === undefined || user.role_level === null) {
    return false;
  }

  // Super admin has all permissions (but only if properly configured)
  if (user.role === ROLES.SUPER_ADMIN || user.is_superuser) {
    return true;
  }

  // Parse user permissions
  const userPermissions = parsePermissions(user.permissions);

  // Check if user has the specific permission
  return userPermissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - User object
 * @param {Array} permissions - Array of permissions to check
 * @returns {boolean} True if user has any of the permissions
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user || !permissions || !Array.isArray(permissions)) return false;
  return permissions.some((permission) => hasPermission(user, permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {Object} user - User object
 * @param {Array} permissions - Array of permissions to check
 * @returns {boolean} True if user has all permissions
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user || !permissions || !Array.isArray(permissions)) return false;
  return permissions.every((permission) => hasPermission(user, permission));
};

/**
 * Check if user has minimum role level
 * @param {Object} user - User object
 * @param {string|number} minRole - Minimum role required (role name or level)
 * @returns {boolean} True if user meets minimum role level
 */
export const hasMinimumRole = (user, minRole) => {
  if (!user || !minRole) return false;

  // For malformed users (missing role_level), be more strict
  // Only allow access if they have an explicit role_level
  if (user.role_level === undefined || user.role_level === null) {
    return false;
  }

  // Get user's role level
  const userLevel = user.role_level;
  
  // Validate user level is within expected range (1-4)
  if (userLevel < 1 || userLevel > 4) {
    return false;
  }
  
  // Get required role level
  let requiredLevel;
  if (typeof minRole === 'number') {
    requiredLevel = minRole;
  } else if (typeof minRole === 'string') {
    requiredLevel = ROLE_LEVELS[minRole];
    // If minRole is not a valid role string, return false for security
    // Invalid roles should not grant access
    if (requiredLevel === undefined) {
      return false;
    }
  } else {
    // null or undefined minRole - return false
    return false;
  }

  // Validate required level is within expected range (1-4)
  if (requiredLevel < 1 || requiredLevel > 4) {
    return false;
  }

  return userLevel >= requiredLevel;
};

/**
 * Check if user has exact role
 * @param {Object} user - User object
 * @param {string} role - Role to check
 * @returns {boolean} True if user has exact role
 */
export const hasRole = (user, role) => {
  if (!user || !role) return false;
  return user.role === role;
};

/**
 * Get user's role display name
 * @param {Object} user - User object
 * @returns {string} Display name for user's role
 */
export const getRoleDisplayName = (user) => {
  if (!user) return "Unknown";

  const roleNames = {
    [ROLES.USER]: "User",
    [ROLES.MODERATOR]: "Moderator",
    [ROLES.ADMIN]: "Administrator",
    [ROLES.SUPER_ADMIN]: "Super Administrator",
  };

  return roleNames[user.role] || "Unknown";
};

/**
 * Get user's role level
 * @param {Object} user - User object
 * @returns {number} Role level (1-4)
 */
export const getUserRoleLevel = (user) => {
  if (!user) return 0;
  
  // For malformed users (missing role_level), be more strict
  // Only return role level if they have an explicit role_level
  if (user.role_level === undefined || user.role_level === null) {
    return 0;
  }
  
  // Validate that role_level is a valid number within expected range
  const roleLevel = Number(user.role_level);
  if (isNaN(roleLevel) || roleLevel < 1 || roleLevel > 4) {
    return 0;
  }
  
  return roleLevel;
};

/**
 * Check if user can access admin features
 * @param {Object} user - User object
 * @returns {boolean} True if user can access admin features
 */
export const canAccessAdmin = (user) => {
  return hasMinimumRole(user, ROLES.ADMIN);
};

/**
 * Check if user can manage users
 * @param {Object} user - User object
 * @returns {boolean} True if user can manage users
 */
export const canManageUsers = (user) => {
  return hasMinimumRole(user, ROLES.ADMIN);
};

/**
 * Check if user can manage database
 * @param {Object} user - User object
 * @returns {boolean} True if user can manage database
 */
export const canManageDatabase = (user) => {
  return hasMinimumRole(user, ROLES.ADMIN);
};

/**
 * Check if user can manage scraper
 * @param {Object} user - User object
 * @returns {boolean} True if user can manage scraper
 */
export const canManageScraper = (user) => {
  return hasMinimumRole(user, ROLES.ADMIN);
};

/**
 * Check if user can restart backend
 * @param {Object} user - User object
 * @returns {boolean} True if user can restart backend
 */
export const canRestartBackend = (user) => {
  return hasMinimumRole(user, ROLES.ADMIN);
};

/**
 * Check if user can access moderator features
 * @param {Object} user - User object
 * @returns {boolean} True if user can access moderator features
 */
export const canAccessModerator = (user) => {
  return hasMinimumRole(user, ROLES.MODERATOR);
};

/**
 * Get all permissions for a role
 * @param {string} role - Role name
 * @returns {Array} Array of permissions for the role
 */
export const getRolePermissions = (role) => {
  const rolePermissions = {
    [ROLES.USER]: [
      PERMISSIONS.VIEW_DEALS,
      PERMISSIONS.TRACK_ITEMS,
      PERMISSIONS.VIEW_PROFILE,
    ],
    [ROLES.MODERATOR]: [
      PERMISSIONS.VIEW_DEALS,
      PERMISSIONS.TRACK_ITEMS,
      PERMISSIONS.VIEW_PROFILE,
      PERMISSIONS.MODERATE_CONTENT,
      PERMISSIONS.VIEW_ANALYTICS,
    ],
    [ROLES.ADMIN]: [
      PERMISSIONS.VIEW_DEALS,
      PERMISSIONS.TRACK_ITEMS,
      PERMISSIONS.VIEW_PROFILE,
      PERMISSIONS.MODERATE_CONTENT,
      PERMISSIONS.VIEW_ANALYTICS,
      PERMISSIONS.MANAGE_USERS,
      PERMISSIONS.MANAGE_DATABASE,
      PERMISSIONS.MANAGE_SCRAPER,
      PERMISSIONS.VIEW_LOGS,
      PERMISSIONS.RESTART_BACKEND,
    ],
    [ROLES.SUPER_ADMIN]: [PERMISSIONS.FULL_ACCESS],
  };

  return rolePermissions[role] || [];
};

// Note: Tests are in separate files: test_rbac_frontend.js and rbac.test.js
